import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { App, Select as AntSelect } from 'antd';
import { Form, Formik, FormikValues } from 'formik';
import * as Yup from 'yup';

import { Button, Select as CustomSelect } from '../../../custom';
import { createUpdatePrivilege, getPermissions, getRoles } from '../request';

interface Props {
  handleClose: () => void;
  refetch: () => void;
}

const AddPrivilege = ({ handleClose, refetch }: Props) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const [rolesQuery, permissionsQuery] = useQueries({
    queries: [
      {
        queryKey: ['get-roles'],
        queryFn: getRoles,
      },
      {
        queryKey: ['get-all-permissions'],
        queryFn: getPermissions,
      },
    ],
  });

  const validate = Yup.object().shape({
    roleId: Yup.string().required('Role is required'),
    permissionIds: Yup.array().min(1, 'At least one permission is required').required('Permission is required'),
  });

  const addPrivilegeMutation = useMutation({
    mutationFn: createUpdatePrivilege,
  });

  const roles = rolesQuery?.data as Role[];
  const permissions = permissionsQuery?.data?.data as Permission[];

  const roleOptions = roles?.map((role) => (
    <option key={role.id} value={role.id}>
      {role.name}
    </option>
  ));

  const permissionOptions = permissions?.map((permission) => ({
    label: permission.name,
    value: permission.id,
  }));

  const addPrivilegeHandler = async (values: FormikValues, resetForm: () => void) => {
    const selectedPermissionId = values.permissionIds?.[0];
    const payload: CreatePrivilegePayload = {
      roleId: values.roleId,
      permissionId: Number(selectedPermissionId),
    };

    try {
      await addPrivilegeMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: 'Success',
            description: data?.message || 'Privilege created successfully',
          });

          queryClient.refetchQueries({ queryKey: ['get-all-privileges'] });
          refetch();

          resetForm();
          handleClose();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'Failed to create privilege',
      });
    }
  };

  return (
    <Formik
      initialValues={{
        roleId: '',
        permissionIds: [],
      }}
      onSubmit={(values, { resetForm }) => {
        addPrivilegeHandler(values, resetForm);
      }}
      validationSchema={validate}
      enableReinitialize={true}
    >
      {({ values, setFieldValue }) => {
        return (
          <Form className="fields">
            <CustomSelect
              name="roleId"
              placeholder="Select Role"
              label="Role"
              options={roleOptions}
              loading={rolesQuery.isLoading}
            />

            <>
              <h5>Permissions</h5>
              <AntSelect
                mode="multiple"
                placeholder="Select Permissions"
                options={permissionOptions}
                value={values.permissionIds}
                onChange={(value) => setFieldValue('permissionIds', value)}
                loading={permissionsQuery.isLoading}
                size="large"
              />
            </>

            <div className="btn-group">
              <Button onClick={handleClose} variant="text" text="Cancel" />
              <Button
                text={addPrivilegeMutation.isPending ? 'Submitting...' : 'Submit'}
                type="submit"
                isLoading={addPrivilegeMutation?.isPending}
                disabled={addPrivilegeMutation?.isPending}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AddPrivilege;
